import React, { useState } from 'react';
import type { Campaign, Account } from '../../store/appStore';
import { useAppStore } from '../../store/appStore';
import {
  PALMESUS_COLORS,
  PALMESUS_FONT,
  PALMESUS_HEADING_TRACKING,
  getPalmesusCampaign,
} from '../../lib/palmesusBrand';

interface Props {
  campaign: Campaign;
  account: Account;
  isQR: boolean;
}

/**
 * Sangønske-skjema. Lys rosa (sekundærfarge) bakgrunn, svart/negativ typo,
 * magenta accent ifølge brand guideline.
 */
export const PalmesusSongRequestForm: React.FC<Props> = ({ campaign, account, isQR }) => {
  const { addSuggestion } = useAppStore();
  const [songTitle, setSongTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = getPalmesusCampaign(campaign.slug);
  const title = meta?.title ?? campaign.title;
  const subtitle = meta?.subtitle ?? campaign.description ?? '';
  const question = meta?.question ?? 'Hvilken sang vil du høre?';

  const canSubmit = songTitle.trim().length > 0 && artist.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const message = `${artist.trim()} – ${songTitle.trim()}`;
      await addSuggestion(
        account.id,
        message,
        null,
        isQR ? 'qr' : 'direct',
        campaign.id,
        null,
      );
      setSongTitle('');
      setArtist('');
      setShowSuccess(true);
    } catch {
      setError('Kunne ikke sende. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center text-center px-6"
        style={{ backgroundColor: PALMESUS_COLORS.pink, color: PALMESUS_COLORS.white }}
      >
        <img
          src="/palmesus/p-logo-white.png"
          alt="Palmesus"
          style={{ width: 'clamp(80px, 18vw, 130px)', height: 'auto', marginBottom: '2rem' }}
        />
        <h2
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 800,
            fontSize: 'clamp(3rem, 14vw, 8rem)',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            lineHeight: 0.95,
            color: PALMESUS_COLORS.white,
            margin: 0,
            textTransform: 'uppercase',
          }}
        >
          Takk!
        </h2>
        <p
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 500,
            fontSize: 'clamp(1rem, 2.6vw, 1.25rem)',
            lineHeight: 1.55,
            color: PALMESUS_COLORS.white,
            opacity: 0.95,
            maxWidth: '30rem',
            margin: '1.5rem 0 2.5rem 0',
          }}
        >
          Sangønsket ditt er registrert. Vi spiller den på Main Stage hvis stjernene står riktig.
        </p>
        <button
          onClick={() => setShowSuccess(false)}
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.9375rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            textTransform: 'uppercase',
            padding: '1rem 1.75rem',
            backgroundColor: PALMESUS_COLORS.white,
            color: PALMESUS_COLORS.pink,
            border: 'none',
            cursor: 'pointer',
            transition: 'all 200ms',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = PALMESUS_COLORS.black;
            e.currentTarget.style.color = PALMESUS_COLORS.white;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = PALMESUS_COLORS.white;
            e.currentTarget.style.color = PALMESUS_COLORS.pink;
          }}
        >
          Send et til
        </button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen px-5 sm:px-10 py-10 sm:py-14"
      style={{
        backgroundColor: PALMESUS_COLORS.softPink,
        color: PALMESUS_COLORS.black,
        paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
      }}
    >
      {/* Topp: logo (svart silhuett mot lys rosa). */}
      <div className="mb-8" style={{ maxWidth: '720px' }}>
        <img
          src="/palmesus/p-logo-black.png"
          alt="Palmesus"
          style={{ width: 'clamp(80px, 18vw, 120px)', height: 'auto', marginBottom: '1.5rem' }}
        />
        <p
          className="uppercase mb-3"
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            color: PALMESUS_COLORS.pink,
          }}
        >
          Sangønske
        </p>
        <h1
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 800,
            fontSize: 'clamp(1.75rem, 9vw, 5.5rem)',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            lineHeight: 0.98,
            color: PALMESUS_COLORS.black,
            margin: 0,
            textTransform: 'uppercase',
            overflowWrap: 'break-word',
            hyphens: 'manual',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 500,
              fontSize: 'clamp(0.9375rem, 2.2vw, 1.0625rem)',
              color: PALMESUS_COLORS.black,
              opacity: 0.7,
              margin: '0.75rem 0 0 0',
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" style={{ maxWidth: '640px' }}>
        {error && (
          <div
            style={{
              backgroundColor: PALMESUS_COLORS.white,
              border: `2px solid ${PALMESUS_COLORS.pink}`,
              padding: '0.75rem 1rem',
            }}
          >
            <p style={{ color: PALMESUS_COLORS.pink, fontSize: '0.875rem', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        <p
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.8125rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            textTransform: 'uppercase',
            color: PALMESUS_COLORS.black,
            margin: 0,
          }}
        >
          {question}
        </p>

        <div>
          <label
            htmlFor="songTitle"
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 700,
              fontSize: '0.8125rem',
              letterSpacing: PALMESUS_HEADING_TRACKING,
              textTransform: 'uppercase',
              color: PALMESUS_COLORS.black,
              display: 'block',
              marginBottom: '0.5rem',
            }}
          >
            Sangtittel
          </label>
          <input
            id="songTitle"
            type="text"
            value={songTitle}
            onChange={(e) => setSongTitle(e.target.value)}
            placeholder="Eks. Absence"
            required
            disabled={isSubmitting}
            autoComplete="off"
            style={{
              width: '100%',
              padding: '1rem 1.25rem',
              fontSize: '1.0625rem',
              fontFamily: PALMESUS_FONT,
              fontWeight: 500,
              border: `2px solid ${PALMESUS_COLORS.black}`,
              backgroundColor: PALMESUS_COLORS.white,
              color: PALMESUS_COLORS.black,
              outline: 'none',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = PALMESUS_COLORS.pink)}
            onBlur={(e) => (e.currentTarget.style.borderColor = PALMESUS_COLORS.black)}
          />
        </div>

        <div>
          <label
            htmlFor="artist"
            style={{
              fontFamily: PALMESUS_FONT,
              fontWeight: 700,
              fontSize: '0.8125rem',
              letterSpacing: PALMESUS_HEADING_TRACKING,
              textTransform: 'uppercase',
              color: PALMESUS_COLORS.black,
              display: 'block',
              marginBottom: '0.5rem',
            }}
          >
            Artist
          </label>
          <input
            id="artist"
            type="text"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            placeholder="Eks. Naked Koala"
            required
            disabled={isSubmitting}
            autoComplete="off"
            style={{
              width: '100%',
              padding: '1rem 1.25rem',
              fontSize: '1.0625rem',
              fontFamily: PALMESUS_FONT,
              fontWeight: 500,
              border: `2px solid ${PALMESUS_COLORS.black}`,
              backgroundColor: PALMESUS_COLORS.white,
              color: PALMESUS_COLORS.black,
              outline: 'none',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = PALMESUS_COLORS.pink)}
            onBlur={(e) => (e.currentTarget.style.borderColor = PALMESUS_COLORS.black)}
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !canSubmit}
          style={{
            fontFamily: PALMESUS_FONT,
            fontWeight: 700,
            fontSize: '0.9375rem',
            letterSpacing: PALMESUS_HEADING_TRACKING,
            textTransform: 'uppercase',
            padding: '1.05rem 1.75rem',
            backgroundColor: PALMESUS_COLORS.pink,
            color: PALMESUS_COLORS.white,
            border: 'none',
            cursor: isSubmitting || !canSubmit ? 'not-allowed' : 'pointer',
            opacity: isSubmitting || !canSubmit ? 0.4 : 1,
            transition: 'all 200ms',
            width: '100%',
          }}
          onMouseOver={(e) => {
            if (!isSubmitting && canSubmit) {
              e.currentTarget.style.backgroundColor = PALMESUS_COLORS.black;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = PALMESUS_COLORS.pink;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {isSubmitting ? 'Sender...' : 'Send sangønske'}
        </button>

        <p
          style={{
            textAlign: 'center',
            color: PALMESUS_COLORS.black,
            opacity: 0.5,
            fontSize: '0.6875rem',
            fontWeight: 500,
            marginTop: '0.5rem',
          }}
        >
          Ved å sende inn godtar du vår{' '}
          <a
            href="/personvern"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline', color: PALMESUS_COLORS.black }}
          >
            personvernerklæring
          </a>
        </p>
      </form>

      <footer
        className="mt-12 pt-6"
        style={{
          fontFamily: PALMESUS_FONT,
          fontWeight: 700,
          fontSize: '0.6875rem',
          letterSpacing: PALMESUS_HEADING_TRACKING,
          color: PALMESUS_COLORS.black,
          opacity: 0.5,
          textTransform: 'uppercase',
          borderTop: `1px solid rgba(0,0,0,0.15)`,
        }}
      >
        Palmesus · Bystranda · 26–27 juni 2026
      </footer>
    </div>
  );
};
